import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Download,
    Edit3,
    Eye,
    FileText,
    Search,
    Send,
    Share2,
    ShieldCheck,
    Trash2,
    X,
} from "lucide-react";
import api from '../../utils/api'
import { getUserPrivileges, getUserRole } from "../../utils/auth";
import Sidebar from "../../components/Sidebar/sidebar";
import Header from "../../components/Header";
import Pagination from "../../components/Pagination/Pagination";
import "./Endorsement.css";
import { API_BASE_URL } from '../../utils/apiBase';
import { MANAGE_POLICY_PRIVILEGES } from "../../utils/managePolicyPrivileges";
import { toast } from '../../components/notifications/notificationService';

const ENDORSEMENT_STATUS_OPTIONS = [
    "Intimated",
    "Document Collection",
    "Approved & Yet to Receive Doc",
    "Approved & Received Doc",
    "Share to Client (Mail)",
    "Delivered to Client"
];

const Endorsement = () => {
    const userRole = getUserRole();
    const userPrivileges = getUserPrivileges();
    const isAdmin = userRole === "admin";
    const hasPrivilege = (key) => isAdmin || userPrivileges.includes(key);
    const canAddEndorsement = hasPrivilege(MANAGE_POLICY_PRIVILEGES.endorsements.add);
    const canViewEndorsement = hasPrivilege(MANAGE_POLICY_PRIVILEGES.endorsements.view);
    const canEditEndorsement = hasPrivilege(MANAGE_POLICY_PRIVILEGES.endorsements.edit);
    const canDownloadEndorsement = hasPrivilege(MANAGE_POLICY_PRIVILEGES.endorsements.downloadCopy);
    const canShareEndorsement = hasPrivilege(MANAGE_POLICY_PRIVILEGES.endorsements.share);
    const canDeleteEndorsement = hasPrivilege(MANAGE_POLICY_PRIVILEGES.endorsements.delete);

    const [collapsed, setCollapsed] = useState(false);

    const [policyData, setPolicyData] = useState(null);
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        endorsementNumber: "",
        endorsementDate: "",
        premiumTransactionDate: "",
        endorsementType: "",
        remarks: "",
        newData: {},
        additionalGrossPremium: "",
        policyId: "",
        clientType: ""
    });

    const [endorsementDoc, setEndorsementDoc] = useState(null);
    const [policySearchInput, setPolicySearchInput] = useState("");
    const [policySuggestions, setPolicySuggestions] = useState([]);
    const [policyFocused, setPolicyFocused] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [showModal, setShowModal] = useState(false);
    const [errors, setErrors] = useState({});
    const [searchError, setSearchError] = useState("");
    const isDeletion = formData.endorsementType === "Deletion";
    const isNil = formData.endorsementType === "Nil";
    const [endorsements, setEndorsements] = useState([]);
    const [editData, setEditData] = useState(null);
    const isEditMode = !!editData;
    const isPolicySelected = !!policyData;
    const [endorsementNoError, setEndorsementNoError] = useState("");
    const [checkingEndorsementNo, setCheckingEndorsementNo] = useState(false);
    const isApproved = formData.status === "Approved";
    const [openStatusId, setOpenStatusId] = useState(null);
    const [openUpwardId, setOpenUpwardId] = useState(null);
    const [statusActionModal, setStatusActionModal] = useState({
        open: false,
        endorsement: null,
        status: "",
        endorsementNumber: "",
        endorsementDoc: null
    });
    const [statusActionError, setStatusActionError] = useState("");
    const setNotification = useCallback(({ message, type }) => {
        (toast[type] || toast.info)(message);
    }, []);
    const [viewEndorsement, setViewEndorsement] = useState(null);
    const [deleteModal, setDeleteModal] = useState({
        open: false,
        endorsement: null,
        confirmationNumber: ""
    });
    const [deleteError, setDeleteError] = useState("");
    const [tableSearch, setTableSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const formatDisplayDate = (date) => {
        if (!date) return "-";

        const parsedDate = new Date(date);
        if (Number.isNaN(parsedDate.getTime())) return "-";

        const day = String(parsedDate.getDate()).padStart(2, "0");
        const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
        const year = parsedDate.getFullYear();

        return `${day}-${month}-${year}`;
    };

    useEffect(() => {
        const fetchSuggestions = async () => {
            if (!policyFocused || policySearchInput.trim().length < 1) {
                setPolicySuggestions([]);
                return;
            }

            try {
                const res = await api.get(
                    `${API_BASE_URL}/api/endorsements/search-policy?query=${policySearchInput}`
                );
                if (res.data.length === 0) {
                    setPolicySuggestions([]);
                    setSearchError("No matching policy found");
                } else {
                    setPolicySuggestions(res.data);
                    setSearchError("");
                }

            } catch (err) {
                console.error("Policy search error:", err);
                setSearchError("Search failed. Try again");
            }
        };

        const delay = setTimeout(fetchSuggestions, 300);
        return () => clearTimeout(delay);
    }, [policySearchInput, policyFocused]);

    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handlePolicySelect = async (policy) => {
        setPolicySearchInput(policy.policyNumber);
        setPolicySuggestions([]);
        setPolicyFocused(false);
        setSearchError("");


        // Load full policy details
        try {
            const res = await api.get(
                `${API_BASE_URL}/api/endorsements/by-number`,
                {
                    params: { number: policy.policyNumber }
                }
            );

            setPolicyData(res.data);

            setFormData(prev => ({
                ...prev,
                policyId: res.data._id,
                clientType: res.data.clientType
            }));

        } catch (err) {
            console.error(err);
            alert("Error loading policy details");
        }
    };

    useEffect(() => {
        if (!formData.endorsementNumber || isEditMode) {
            setEndorsementNoError("");
            return;
        }

        const delay = setTimeout(async () => {
            try {
                setCheckingEndorsementNo(true);

                const res = await api.get(
                    `${API_BASE_URL}/api/endorsements/check-endorsement-number`,
                    {
                        params: {
                            number: formData.endorsementNumber,
                            excludeId: editData?._id
                        }
                    }
                );

                if (res.data.exists) {
                    setEndorsementNoError("Endorsement number already exists");
                } else {
                    setEndorsementNoError("");
                }

            } catch (err) {
                console.error(err);
            } finally {
                setCheckingEndorsementNo(false);
            }
        }, 500); // ⏱ debounce

        return () => clearTimeout(delay);

    }, [formData.endorsementNumber, isEditMode, editData?._id]);

    const openEditEndorsement = (e) => {
        setEditData(e);
        setShowModal(true);

        // lock policy
        const policyId = e.policyId?._id || e.policyId;
        setPolicyData({
            _id: policyId,
            clientName: e.policyId?.clientName,
            policyNumber: e.policyId?.policyNumber,
            policyStartDate: e.policyId?.policyStartDate,
            policyEndDate: e.policyId?.policyEndDate
        });

        setFormData({
            endorsementNumber: e.endorsementNumber,
            endorsementType: e.endorsementType,
            endorsementDate: e.endorsementDate?.split("T")[0],
            premiumTransactionDate: e.premiumTransactionDate?.split("T")[0] || "",
            remarks: e.remarks || "",
            newData: e.newData || {},
            additionalGrossPremium: e.additionalGrossPremium,
            policyId: policyId,
            clientType: e.clientType
        });
    };

    const openViewEndorsement = (endorsement) => {
        setViewEndorsement(endorsement);
    };

    const openDeleteModal = (endorsement) => {
        setDeleteModal({
            open: true,
            endorsement,
            confirmationNumber: ""
        });
        setDeleteError("");
    };



    const validate = (data) => {
        const errs = {};

        if (!data.policyId) {
            errs.policy = "Please select a policy";
        }

        if (!data.endorsementDate) {
            errs.endorsementDate = "Start date of endorsement is required";
        }


        if (!data.endorsementType) {
            errs.endorsementType = "Please select endorsement type";
        }

        if (!isNil && !data.additionalGrossPremium) {
            errs.additionalGrossPremium =
                data.endorsementType === "Deletion"
                    ? "Refund amount is required"
                    : "Additional premium is required";
        }


        // if (!data.newData?.updatedField?.trim()) {
        //     errs.updatedField = "Updated value is required";
        // }

        if (
            data.additionalGrossPremium &&
            Number(data.additionalGrossPremium) < 0
        ) {
            errs.additionalGrossPremium = "Premium cannot be negative";
        }

        // if (!isNil && Object.keys(data.newData || {}).length === 0) {
        //     errs.updatedField = "Updated value is required";
        // }

        if (data.status === "Approved" && !endorsementDoc && !editData?.endorsementDocPath) {
            errs.endorsementDoc = "Endorsement document is mandatory for Approved status";
        }



        return errs;
    };

    const resetForm = () => {
        setPolicyData(null);
        setPolicySearchInput("");
        setPolicySuggestions([]);
        setPolicyFocused(false);
        setSelectedIndex(-1);

        setFormData({
            endorsementNumber: "",
            endorsementDate: "",
            premiumTransactionDate: "",
            endorsementType: "",
            remarks: "",
            newData: {},
            additionalGrossPremium: "",
            policyId: "",
            clientType: ""
        });

        setEndorsementDoc(null);
        setErrors({});
    };

    const fetchEndorsements = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get(`${API_BASE_URL}/api/endorsements`);
            setEndorsements(res.data);
        } catch (err) {
            console.error("Failed to fetch endorsements", err);
            setNotification({ show: true, message: "Failed to fetch endorsements", type: "error" });
        } finally {
            setLoading(false);
        }
    }, [setNotification]);

    useEffect(() => {
        fetchEndorsements();
    }, [fetchEndorsements]);

    const handleDeleteEndorsement = async () => {
        const endorsement = deleteModal.endorsement;
        if (!endorsement?._id) return;

        const expectedNumber = String(endorsement.endorsementNumber || "").trim();
        const enteredNumber = String(deleteModal.confirmationNumber || "").trim();

        if (enteredNumber !== expectedNumber) {
            setDeleteError("Entered endorsement number does not match.");
            return;
        }

        try {
            await api.delete(`${API_BASE_URL}/api/endorsements/delete/${endorsement._id}`);
            setDeleteModal({
                open: false,
                endorsement: null,
                confirmationNumber: ""
            });
            setDeleteError("");
            setNotification({ show: true, message: "Endorsement deleted successfully.", type: "success" });
            fetchEndorsements();
        } catch (err) {
            setDeleteError(err?.response?.data?.error || "Failed to delete endorsement");
        }
    };

    const closeStatusActionModal = () => {
        setStatusActionModal({
            open: false,
            endorsement: null,
            status: "",
            endorsementNumber: "",
            endorsementDoc: null
        });
        setStatusActionError("");
    };

    const submitStatusChange = async ({ endorsement, status, endorsementNumber = "", endorsementDoc = null }) => {
        if (!endorsement?._id) return;

        if (status === "Approved & Yet to Receive Doc" && !String(endorsementNumber || "").trim()) {
            setStatusActionError("Endorsement number is required.");
            return;
        }

        if (status === "Approved & Received Doc" && !endorsementDoc) {
            setStatusActionError("Endorsement document is required.");
            return;
        }

        try {
            if (endorsementDoc) {
                const payload = new FormData();
                payload.append("status", status);
                payload.append("endorsementDoc", endorsementDoc);

                await api.put(
                    `${API_BASE_URL}/api/endorsements/update-status/${endorsement._id}`,
                    payload,
                    { headers: { "Content-Type": "multipart/form-data" } }
                );
            } else {
                await api.put(
                    `${API_BASE_URL}/api/endorsements/update-status/${endorsement._id}`,
                    {
                        status,
                        endorsementNumber
                    }
                );
            }

            closeStatusActionModal();
            setOpenStatusId(null);
            setOpenUpwardId(null);
            fetchEndorsements();
        } catch (err) {
            const message =
                err?.response?.data?.error || "Failed to update endorsement status";
            setStatusActionError(message);
            setNotification({ show: true, message, type: "error" });
        }
    };

    const handleStatusSelection = async (endorsement, status) => {
        setOpenStatusId(null);
        setOpenUpwardId(null);

        if (status === "Document Collection" || status === "Intimated" || status === "Share to Client (Mail)" || status === "Delivered to Client") {
            await submitStatusChange({ endorsement, status });
            return;
        }

        if (status === "Approved & Yet to Receive Doc") {
            setStatusActionModal({
                open: true,
                endorsement,
                status,
                endorsementNumber: endorsement.endorsementNumber || "",
                endorsementDoc: null
            });
            setStatusActionError("");
            return;
        }

        if (status === "Approved & Received Doc") {
            setStatusActionModal({
                open: true,
                endorsement,
                status,
                endorsementNumber: "",
                endorsementDoc: null
            });
            setStatusActionError("");
        }
    };

    const getClientName = useCallback((endorsement) => endorsement?.policyId?.clientName || "-", []);

    const getPolicyNumber = useCallback((endorsement) => endorsement?.policyId?.policyNumber || "-", []);

    const getInitials = (name = "") => {
        const parts = String(name).trim().split(/\s+/).filter(Boolean);
        if (!parts.length) return "EN";
        return parts.slice(0, 2).map((part) => part[0]).join("").toUpperCase();
    };

    const getStatusTone = (status = "") => {
        const normalized = status.toLowerCase();
        if (normalized.includes("delivered") || normalized.includes("received")) return "approved";
        if (normalized.includes("approved")) return "approved";
        if (normalized.includes("rejected") || normalized.includes("disabled")) return "rejected";
        if (normalized.includes("document")) return "document";
        if (normalized.includes("share")) return "shared";
        return "pending";
    };

    const getExpiryMeta = (endorsement) => {
        const endDate = endorsement?.policyId?.policyEndDate ? new Date(endorsement.policyId.policyEndDate) : null;
        if (!endDate || Number.isNaN(endDate.getTime())) {
            return { label: "No expiry", tone: "muted", days: null };
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);

        const days = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
        if (days < 0) return { label: `${Math.abs(days)} days overdue`, tone: "danger", days };
        if (days <= 30) return { label: `${days} days left`, tone: "warning", days };
        return { label: `${days} days left`, tone: "success", days };
    };

    const formatCurrency = (value) => {
        const amount = Number(value || 0);
        if (!amount) return "Rs. 0";
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            maximumFractionDigits: 0,
        }).format(amount).replace("₹", "Rs. ");
    };

    const typeOptions = useMemo(() => (
        [...new Set(endorsements.map((e) => e.endorsementType).filter(Boolean))]
            .sort((a, b) => a.localeCompare(b))
    ), [endorsements]);

    const statusOptions = useMemo(() => (
        [...new Set(endorsements.map((e) => e.status).filter(Boolean))]
            .sort((a, b) => a.localeCompare(b))
    ), [endorsements]);

    const filteredEndorsements = useMemo(() => {
        const search = tableSearch.trim().toLowerCase();

        return endorsements.filter((endorsement) => {
            if (typeFilter !== "all" && endorsement.endorsementType !== typeFilter) return false;
            if (statusFilter !== "all" && endorsement.status !== statusFilter) return false;

            if (dateFrom || dateTo) {
                const endorsementDate = endorsement.endorsementDate ? new Date(endorsement.endorsementDate) : null;
                if (!endorsementDate || Number.isNaN(endorsementDate.getTime())) return false;

                const from = dateFrom ? new Date(dateFrom) : null;
                const to = dateTo ? new Date(dateTo) : null;
                if (from && endorsementDate < from) return false;
                if (to) {
                    const endOfDay = new Date(to);
                    endOfDay.setHours(23, 59, 59, 999);
                    if (endorsementDate > endOfDay) return false;
                }
            }

            if (!search) return true;

            return [
                getClientName(endorsement),
                endorsement.endorsementNumber,
                getPolicyNumber(endorsement),
            ].some((value) => String(value || "").toLowerCase().includes(search));
        });
    }, [dateFrom, dateTo, endorsements, getClientName, getPolicyNumber, statusFilter, tableSearch, typeFilter]);

    const pageLast = currentPage * itemsPerPage;
    const pageFirst = pageLast - itemsPerPage;
    const currentEndorsements = filteredEndorsements.slice(pageFirst, pageLast);

    useEffect(() => {
        setCurrentPage(1);
    }, [tableSearch, typeFilter, statusFilter, dateFrom, dateTo, itemsPerPage]);

    useEffect(() => {
        const totalPages = Math.max(1, Math.ceil(filteredEndorsements.length / itemsPerPage));
        setCurrentPage((page) => Math.min(page, totalPages));
    }, [filteredEndorsements.length, itemsPerPage]);

    const summaryCards = useMemo(() => {
        const expiringSoon = endorsements.filter((endorsement) => {
            const expiry = getExpiryMeta(endorsement);
            return typeof expiry.days === "number" && expiry.days >= 0 && expiry.days <= 30;
        }).length;

        return [
            { label: "Total Endorsements", value: endorsements.length, icon: FileText, tone: "blue" },
            { label: "Pending Endorsements", value: endorsements.filter((e) => getStatusTone(e.status) === "pending" || getStatusTone(e.status) === "document").length, icon: Search, tone: "amber" },
            { label: "Approved", value: endorsements.filter((e) => getStatusTone(e.status) === "approved").length, icon: ShieldCheck, tone: "green" },
            { label: "Rejected", value: endorsements.filter((e) => getStatusTone(e.status) === "rejected").length, icon: X, tone: "red" },
            { label: "Expiring Soon", value: expiringSoon, icon: Send, tone: "violet" },
        ];
    }, [endorsements]);

    const activeFilterChips = [
        tableSearch && { key: "search", label: `Search: ${tableSearch}`, onClear: () => setTableSearch("") },
        typeFilter !== "all" && { key: "type", label: `Type: ${typeFilter}`, onClear: () => setTypeFilter("all") },
        statusFilter !== "all" && { key: "status", label: `Status: ${statusFilter}`, onClear: () => setStatusFilter("all") },
        (dateFrom || dateTo) && { key: "date", label: `${dateFrom || "Any"} to ${dateTo || "Any"}`, onClear: () => { setDateFrom(""); setDateTo(""); } },
    ].filter(Boolean);

    const resetTableFilters = () => {
        setTableSearch("");
        setTypeFilter("all");
        setStatusFilter("all");
        setDateFrom("");
        setDateTo("");
    };

    const toggleRowSelection = (endorsementId) => {
        setSelectedRows((prev) =>
            prev.includes(endorsementId)
                ? prev.filter((id) => id !== endorsementId)
                : [...prev, endorsementId]
        );
    };

    const toggleAllRows = () => {
        const availableIds = currentEndorsements
            .filter((endorsement) => endorsement.status !== "Disabled")
            .map((endorsement) => endorsement._id);
        const allSelected = availableIds.length > 0 && availableIds.every((id) => selectedRows.includes(id));
        setSelectedRows((prev) => {
            if (allSelected) return prev.filter((id) => !availableIds.includes(id));
            return [...new Set([...prev, ...availableIds])];
        });
    };

    const handleDownloadEndorsement = async (endorsement) => {
        if (!endorsement?.endorsementDocPath) {
            setNotification({ show: true, message: "Endorsement document is not uploaded yet.", type: "error" });
            return;
        }

        try {
            const response = await api.get(`${API_BASE_URL}/api/endorsements/download/${endorsement._id}`, {
                responseType: "blob"
            });
            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = endorsement.endorsementDocPath.split(/[\\/]/).pop() || "endorsement-document";
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            setNotification({
                show: true,
                message: err?.response?.data?.error || "Failed to download endorsement document",
                type: "error"
            });
        }
    };

    const handleShareEndorsement = async (endorsement) => {
        if (endorsement.status === "Disabled") return;
        await handleStatusSelection(endorsement, "Share to Client (Mail)");
    };





    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationErrors = validate(formData);
        setErrors(validationErrors);


        if (Object.keys(validationErrors).length > 0) {
            return;
        }

        const payload = new FormData();

        Object.entries(formData).forEach(([key, val]) =>
            payload.append(
                key,
                typeof val === "object" ? JSON.stringify(val) : val
            )
        );

        payload.append("endorsementDoc", endorsementDoc);

        if (formData.endorsementType === "Deletion") {
            payload.set(
                "additionalGrossPremium",
                -Math.abs(formData.additionalGrossPremium)
            );
        }

        try {
            if (editData) {
                await api.put(
                    `${API_BASE_URL}/api/endorsements/update/${editData._id}`,
                    payload,
                    { headers: { "Content-Type": "multipart/form-data" } }
                );
            } else {
                await api.post(
                    `${API_BASE_URL}/api/endorsements/add`,
                    payload,
                    { headers: { "Content-Type": "multipart/form-data" } }
                );
            }

            alert("Endorsement Saved Successfully!");
            setShowModal(false);
            resetForm();
            fetchEndorsements();
            setEditData(null);

        } catch (err) {
            console.error(err);
            alert("Error adding endorsement");
        }
    };


    return (
        <div className="endorsement-container">
            <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />

            <div className={`endorsement-content ${collapsed ? "collapsed" : ""}`}>
                <div className="endorsement-sticky-header">
                    <Header />
                </div>
                <div className="endorsement-scroll-area">
                    <section className="endorsement-crm-section">
                        <div className="endorsement-page-hero">
                            <div>
                                <span className="endorsement-eyebrow">Manage Policy / Endorsements</span>
                                <h3>Policy Endorsements</h3>
                                <p>{filteredEndorsements.length} of {endorsements.length} endorsements visible</p>
                            </div>
                            {canAddEndorsement && (
                                <button
                                    className="open-endorsement-btn"
                                    onClick={() => setShowModal(true)}
                                >
                                    + Add Endorsement
                                </button>
                            )}
                        </div>

                        <div className="endorsement-summary-grid">
                            {summaryCards.map((card) => {
                                const Icon = card.icon;
                                return (
                                    <article className={`endorsement-summary-card ${card.tone}`} key={card.label}>
                                        <span><Icon size={18} /></span>
                                        <div>
                                            <strong>{card.value}</strong>
                                            <small>{card.label}</small>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>

                        <div className="endorsement-filter-panel">
                            <div className="endorsement-search-field">
                                <Search size={18} />
                                <input
                                    value={tableSearch}
                                    onChange={(e) => setTableSearch(e.target.value)}
                                    placeholder="Search by client name or endorsement number..."
                                />
                            </div>
                            <div className="endorsement-filter-grid">
                                <div>
                                    <label>Endorsement Type</label>
                                    <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                                        <option value="all">All Types</option>
                                        {typeOptions.map((type) => <option key={type} value={type}>{type}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label>Status</label>
                                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                        <option value="all">All Statuses</option>
                                        {statusOptions.map((status) => <option key={status} value={status}>{status}</option>)}
                                    </select>
                                </div>
                                <div className="endorsement-date-filter">
                                    <label>Start Date Range</label>
                                    <div>
                                        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} aria-label="From endorsement start date" />
                                        <span>to</span>
                                        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} aria-label="To endorsement start date" />
                                    </div>
                                </div>
                                <button type="button" className="endorsement-reset-btn" onClick={resetTableFilters}>
                                    Reset filters
                                </button>
                            </div>
                            {activeFilterChips.length > 0 && (
                                <div className="endorsement-filter-chips">
                                    {activeFilterChips.map((chip) => (
                                        <button type="button" key={chip.key} onClick={chip.onClear}>
                                            {chip.label}
                                            <X size={13} />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {selectedRows.length > 0 && (
                            <div className="endorsement-bulkbar">
                                <strong>{selectedRows.length} selected</strong>
                                <span>Use row actions for view, edit, document download, sharing, or delete.</span>
                            </div>
                        )}

                        {loading ? (
                            <div className="endorsement-skeleton-list" aria-label="Loading endorsements">
                                {[1, 2, 3, 4, 5].map((item) => <span key={item} />)}
                            </div>
                        ) : filteredEndorsements.length === 0 ? (
                            <div className="endorsement-empty-state">
                                <FileText size={38} />
                                <h3>No endorsements found</h3>
                                <p>Try clearing filters or widening the endorsement start date range.</p>
                                <button type="button" onClick={resetTableFilters}>Clear all filters</button>
                            </div>
                        ) : (
                            <div className="endorsement-table-shell">
                                <table className="endorsement-table">
                                    <thead>
                                        <tr>
                                            <th className="endorsement-sticky-check">
                                                <input
                                                    type="checkbox"
                                                    aria-label="Select all endorsements"
                                                    checked={currentEndorsements.filter((e) => e.status !== "Disabled").length > 0 && currentEndorsements.filter((e) => e.status !== "Disabled").every((e) => selectedRows.includes(e._id))}
                                                    onChange={toggleAllRows}
                                                />
                                            </th>
                                            <th className="endorsement-sticky-name">Name</th>
                                            <th>Endorsement Number</th>
                                            <th>Policy Number</th>
                                            <th>Endorsement Type</th>
                                            <th>Premium</th>
                                            <th>Start Date of Endorsement</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {currentEndorsements.map((e) => {
                                            const clientName = getClientName(e);
                                            const expiry = getExpiryMeta(e);
                                            const isSelected = selectedRows.includes(e._id);
                                            const isDisabled = e.status === "Disabled";
                                            const isStatusOpen = openStatusId === e._id;

                                            return (
                                                <tr key={e._id} className={`${isDisabled ? "row-disabled" : ""} ${isSelected ? "is-selected" : ""} ${isStatusOpen ? "is-status-open" : ""}`}>
                                                    <td className="endorsement-sticky-check">
                                                        {!isDisabled && (
                                                            <input
                                                                type="checkbox"
                                                                aria-label={`Select ${clientName}`}
                                                                checked={isSelected}
                                                                onChange={() => toggleRowSelection(e._id)}
                                                            />
                                                        )}
                                                    </td>
                                                    <td className="endorsement-sticky-name">
                                                        <div className="endorsement-client-cell">
                                                            <span className="endorsement-avatar">{getInitials(clientName)}</span>
                                                            <div>
                                                                <strong>{clientName}</strong>
                                                                <small>{e.clientType || "Policy Client"}</small>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td data-label="Endorsement Number">
                                                        <button
                                                            type="button"
                                                            className={`endorsement-link ${isDisabled ? "disabled" : ""}`}
                                                            disabled={isDisabled}
                                                            onClick={() => canViewEndorsement && openViewEndorsement(e)}
                                                        >
                                                            {e.endorsementNumber || "-"}
                                                        </button>
                                                    </td>
                                                    <td data-label="Policy Number">{getPolicyNumber(e)}</td>
                                                    <td data-label="Endorsement Type"><span className="endorsement-type-pill">{e.endorsementType || "-"}</span></td>
                                                    <td data-label="Premium"><strong className="endorsement-premium">{formatCurrency(e.additionalGrossPremium)}</strong></td>
                                                    <td data-label="Start Date of Endorsement">
                                                        <div className="endorsement-date-cell">
                                                            <strong>{formatDisplayDate(e.endorsementDate)}</strong>
                                                            <span className={`endorsement-expiry-pill ${expiry.tone}`}>{expiry.label}</span>
                                                        </div>
                                                    </td>
                                                    <td data-label="Status" className="endorsement-status-cell">
                                                        <div
                                                            className={`status-dd-inline endorsement-status-control ${getStatusTone(e.status)} ${e.status.replace(/\s/g, "").toLowerCase()} ${openUpwardId === e._id ? "open-up" : ""}`}
                                                            onClick={(ev) => {
                                                                if (isDisabled || (!canEditEndorsement && !canShareEndorsement)) return;
                                                                ev.stopPropagation();

                                                                const rect = ev.currentTarget.getBoundingClientRect();
                                                                const spaceBelow = window.innerHeight - rect.bottom;

                                                                if (spaceBelow < 220) {
                                                                    setOpenUpwardId(e._id);
                                                                } else {
                                                                    setOpenUpwardId(null);
                                                                }

                                                                setOpenStatusId(openStatusId === e._id ? null : e._id);
                                                            }}
                                                        >
                                                            <div className="status-dd-trigger">
                                                                {e.status || "Pending"}
                                                            </div>

                                                            {isStatusOpen && (
                                                                <div className="status-dd-menu">
                                                                    {ENDORSEMENT_STATUS_OPTIONS
                                                                        .filter((s) => s !== "Share to Client (Mail)" || canShareEndorsement)
                                                                        .map((s) => (
                                                                        <div
                                                                            key={s}
                                                                            className={`status-dd-item ${s === e.status ? "active" : ""}`}
                                                                            onClick={async () => {
                                                                                await handleStatusSelection(e, s);
                                                                            }}
                                                                        >
                                                                            {s}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td data-label="Actions">
                                                        <div className="endorsement-action-group">
                                                            {canViewEndorsement && <button type="button" title="View" aria-label="View" disabled={isDisabled} onClick={() => openViewEndorsement(e)}><Eye size={16} /></button>}
                                                            {canEditEndorsement && <button type="button" title="Edit" aria-label="Edit" disabled={isDisabled} onClick={() => openEditEndorsement(e)}><Edit3 size={16} /></button>}
                                                            {canDownloadEndorsement && <button type="button" title="Download" aria-label="Download" disabled={isDisabled} onClick={() => handleDownloadEndorsement(e)}><Download size={16} /></button>}
                                                            {canShareEndorsement && <button type="button" title="Share" aria-label="Share" disabled={isDisabled} onClick={() => handleShareEndorsement(e)}><Share2 size={16} /></button>}
                                                            {canDeleteEndorsement && <button type="button" className="danger" title="Delete" aria-label="Delete" disabled={isDisabled} onClick={() => openDeleteModal(e)}><Trash2 size={16} /></button>}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {filteredEndorsements.length > 0 && (
                            <div className="endorsement-pagination-row">
                                <div className="endorsement-pagination-meta">
                                    <span>
                                        Showing {pageFirst + 1}-{Math.min(pageLast, filteredEndorsements.length)} of {filteredEndorsements.length}
                                    </span>
                                    <small>Filtered endorsement records</small>
                                </div>

                                <div className="endorsement-pagination-controls">
                                    <Pagination
                                        totalItems={filteredEndorsements.length}
                                        itemsPerPage={itemsPerPage}
                                        currentPage={currentPage}
                                        onPageChange={setCurrentPage}
                                    />
                                </div>

                                <div className="endorsement-items-per-page">
                                    <label>Items per page:</label>
                                    <select
                                        value={itemsPerPage}
                                        onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                    >
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </select>
                                </div>
                            </div>
                        )}

                        {deleteModal.open && (
                            <div className="modal-overlay-endor">
                                <div className="modal-box-endor endorsement-confirm-modal">
                                    <div className="modal-header-endor">
                                        <div>
                                            <span className="modal-eyebrow-endor">Confirmation</span>
                                            <h3>Delete Endorsement</h3>
                                        </div>
                                        <button
                                            className="close-btn"
                                            onClick={() => {
                                                setDeleteModal({
                                                    open: false,
                                                    endorsement: null,
                                                    confirmationNumber: ""
                                                });
                                                setDeleteError("");
                                            }}
                                        >
                                            ×
                                        </button>
                                    </div>

                                    <div className="modal-body-scroll-endor">
                                        <div className="endorsement-warning-panel">
                                            <strong>{deleteModal.endorsement?.endorsementNumber || "Selected endorsement"}</strong>
                                            <p>Please enter the endorsement number to confirm deletion.</p>
                                        </div>
                                        <label className="endorsement-field">
                                            <span>Endorsement Number</span>
                                            <input
                                                type="text"
                                                value={deleteModal.confirmationNumber}
                                                onChange={(e) => {
                                                    setDeleteModal((prev) => ({
                                                        ...prev,
                                                        confirmationNumber: e.target.value
                                                    }));
                                                    setDeleteError("");
                                                }}
                                                placeholder="Enter endorsement number"
                                            />
                                        </label>

                                        {deleteError && (
                                            <small className="error-text">{deleteError}</small>
                                        )}

                                        <div className="modal-footer-endor">
                                            <button
                                                type="button"
                                                className="submit-btn-endor"
                                                onClick={handleDeleteEndorsement}
                                            >
                                                Confirm Delete
                                            </button>
                                            <button
                                                type="button"
                                                className="cancel-btn-endor"
                                                onClick={() => {
                                                    setDeleteModal({
                                                        open: false,
                                                        endorsement: null,
                                                        confirmationNumber: ""
                                                    });
                                                    setDeleteError("");
                                                }}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {viewEndorsement && (
                            <div className="modal-overlay-endor">
                                <div className="modal-box-endor endorsement-view-modal">
                                    <div className="modal-header-endor">
                                        <div>
                                            <span className="modal-eyebrow-endor">Policy Record</span>
                                            <h3>View Endorsement</h3>
                                        </div>
                                        <button
                                            className="close-btn"
                                            onClick={() => setViewEndorsement(null)}
                                        >
                                            ×
                                        </button>
                                    </div>

                                    <div className="modal-body-scroll-endor">
                                        <div className="policy-details-box full-width">
                                            <h4>Policy Details</h4>
                                            <div className="policy-details-grid">
                                                <p><strong>Client:</strong> {viewEndorsement.policyId?.clientName || "-"}</p>
                                                <p><strong>Policy No:</strong> {viewEndorsement.policyId?.policyNumber || "-"}</p>
                                                <p><strong>Status:</strong> {viewEndorsement.status || "-"}</p>
                                                <p><strong>Type:</strong> {viewEndorsement.endorsementType || "-"}</p>
                                                <p><strong>Endorsement No:</strong> {viewEndorsement.endorsementNumber || "-"}</p>
                                                <p><strong>Start Date of Endorsement:</strong> {formatDisplayDate(viewEndorsement.endorsementDate)}</p>
                                                <p><strong>Premium Transaction Date:</strong> {formatDisplayDate(viewEndorsement.premiumTransactionDate)}</p>
                                                <p><strong>Premium:</strong> {viewEndorsement.additionalGrossPremium ?? "-"}</p>
                                                <p><strong>Remarks:</strong> {viewEndorsement.remarks || "-"}</p>
                                            </div>
                                        </div>

                                        {viewEndorsement.endorsementDocPath && (
                                            <div className="endorsement-doc-view">
                                                <a
                                                    href={`${API_BASE_URL}/${viewEndorsement.endorsementDocPath}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="view-doc-btn"
                                                >
                                                    View Document
                                                </a>
                                            </div>
                                        )}

                                        <div className="modal-footer-endor">
                                            <button
                                                type="button"
                                                className="cancel-btn-endor"
                                                onClick={() => setViewEndorsement(null)}
                                            >
                                                Close
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {statusActionModal.open && (
                            <div className="modal-overlay-endor">
                                <div className="modal-box-endor endorsement-status-modal">
                                    <div className="modal-header-endor">
                                        <div>
                                            <span className="modal-eyebrow-endor">Status Update</span>
                                            <h3>{statusActionModal.status}</h3>
                                        </div>
                                        <button
                                            className="close-btn"
                                            onClick={closeStatusActionModal}
                                        >
                                            ×
                                        </button>
                                    </div>

                                    <div className="modal-body-scroll-endor">
                                        {statusActionModal.status === "Approved & Yet to Receive Doc" && (
                                            <label className="endorsement-field">
                                                <span>Endorsement Number <b>*</b></span>
                                                <input
                                                    type="text"
                                                    value={statusActionModal.endorsementNumber}
                                                    onChange={(e) => {
                                                        setStatusActionModal((prev) => ({
                                                            ...prev,
                                                            endorsementNumber: e.target.value
                                                        }));
                                                        setStatusActionError("");
                                                    }}
                                                />
                                            </label>
                                        )}

                                        {statusActionModal.status === "Approved & Received Doc" && (
                                            <label className="endorsement-field">
                                                <span>Upload Endorsement Document <b>*</b></span>
                                                <input
                                                    type="file"
                                                    onChange={(e) => {
                                                        setStatusActionModal((prev) => ({
                                                            ...prev,
                                                            endorsementDoc: e.target.files?.[0] || null
                                                        }));
                                                        setStatusActionError("");
                                                    }}
                                                />
                                            </label>
                                        )}

                                        {statusActionError && (
                                            <small className="error-text">{statusActionError}</small>
                                        )}

                                        <div className="modal-footer-endor">
                                            <button
                                                type="button"
                                                className="submit-btn-endor"
                                                onClick={() =>
                                                    submitStatusChange({
                                                        endorsement: statusActionModal.endorsement,
                                                        status: statusActionModal.status,
                                                        endorsementNumber: statusActionModal.endorsementNumber,
                                                        endorsementDoc: statusActionModal.endorsementDoc
                                                    })
                                                }
                                            >
                                                Save
                                            </button>
                                            <button
                                                type="button"
                                                className="cancel-btn-endor"
                                                onClick={closeStatusActionModal}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}



                        {/* STEP 1 — ENTER POLICY NUMBER */}



                        {/* STEP 2 — POLICY DETAILS */}
                        {loading && <p>Loading policy...</p>}
                        {showModal && (
                            <div className="modal-overlay-endor">
                                <div className="modal-box-endor endorsement-form-modal">

                                    <div className="modal-header-endor">
                                        <div>
                                            <span className="modal-eyebrow-endor">{isEditMode ? "Edit Record" : "New Request"}</span>
                                            <h3>{isEditMode ? "Edit Policy Endorsement" : "Add Policy Endorsement"}</h3>
                                        </div>
                                        <button
                                            className="close-btn"
                                            onClick={() => setShowModal(false)}
                                        >
                                            ✕
                                        </button>
                                    </div>

                                    <div className="modal-body-scroll-endor">


                                        {/* 🔹 STEP 3 — ENDORSEMENT FORM */}
                                        <form id="endorsement-form" className="endorsement-form" onSubmit={handleSubmit}>

                                            {/* 🔹 STEP 1 — POLICY SEARCH */}
                                            {/* 🔹 STEP 1 — POLICY SEARCH */}
                                            {!isEditMode && (
                                                <label className="endorsement-form-field full-width">
                                                    <span>Search Policy <b>*</b></span>
                                                    <div className="endorsement-policy-search-wrap">
                                                        <input
                                                            type="text"
                                                            value={policySearchInput}
                                                            placeholder="Client name + Policy Number (, Separated)"
                                                            onChange={(e) => {
                                                                setPolicySearchInput(e.target.value);
                                                                setSelectedIndex(-1);
                                                            }}
                                                            onFocus={() => setPolicyFocused(true)}
                                                            onBlur={() => {
                                                                setTimeout(() => {
                                                                    setPolicySuggestions([]);
                                                                    setPolicyFocused(false);
                                                                }, 200);
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "ArrowDown") {
                                                                    setSelectedIndex(prev =>
                                                                        Math.min(prev + 1, policySuggestions.length - 1)
                                                                    );
                                                                } else if (e.key === "ArrowUp") {
                                                                    setSelectedIndex(prev =>
                                                                        Math.max(prev - 1, 0)
                                                                    );
                                                                } else if (e.key === "Enter" && selectedIndex !== -1) {
                                                                    handlePolicySelect(policySuggestions[selectedIndex]);
                                                                }
                                                            }}
                                                        />

                                                        {searchError && (
                                                            <small className="error-text">{searchError}</small>
                                                        )}

                                                        {policyFocused && policySuggestions.length > 0 && (
                                                            <ul className="policy-suggestion-box-endor">
                                                                {policySuggestions.map((item, idx) => (
                                                                    <li
                                                                        key={idx}
                                                                        className={selectedIndex === idx ? "highlighted" : ""}
                                                                        onMouseDown={() => handlePolicySelect(item)}
                                                                    >
                                                                        <b>{item.policyNumber}</b>
                                                                        <br />
                                                                        <small>{item.insurerName}</small>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                    </div>
                                                </label>
                                            )}


                                            {/* {!isEditMode && !policyData && (
                                    <div className="info-box">
                                        Please search and select a policy to enable endorsement form.
                                    </div>
                                )} */}


                                            {/* 🔹 STEP 2 — POLICY DETAILS */}
                                            {!isEditMode && policyData && (
                                                <div className="policy-details-box full-width">
                                                    <h4>Policy Details</h4>

                                                    <div className="policy-details-grid">
                                                        <p><strong>Client:</strong> {policyData.clientName}</p>
                                                        <p><strong>Insurer:</strong> {policyData.insurerName}</p>
                                                        <p><strong>Policy Name:</strong> {policyData.policyName}</p>
                                                        <p><strong>Premium:</strong> ₹{policyData.grossPremium}</p>
                                                    </div>
                                                </div>
                                            )}

                                            {isEditMode && (
                                                <div className="policy-details-box full-width">
                                                    <h4>Policy Details</h4>
                                                    <div className="policy-details-grid">
                                                        <p><strong>Client:</strong> {policyData?.clientName || "-"}</p>
                                                        <p><strong>Policy No:</strong> {policyData?.policyNumber || "-"}</p>
                                                        <p>
                                                            <strong>Start Date:</strong>{" "}
                                                            {policyData?.policyStartDate
                                                                ? new Date(policyData.policyStartDate).toLocaleDateString()
                                                                : "-"}
                                                        </p>
                                                        <p>
                                                            <strong>End Date:</strong>{" "}
                                                            {policyData?.policyEndDate
                                                                ? new Date(policyData.policyEndDate).toLocaleDateString()
                                                                : "-"}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}


                                            <label className="endorsement-form-field">
                                                <span>Endorsement Number</span>
                                                <input
                                                    type="text"
                                                    name="endorsementNumber"
                                                    value={formData.endorsementNumber}
                                                    onChange={handleChange}
                                                    disabled={!isPolicySelected}
                                                    className={
                                                        endorsementNoError || errors.endorsementNumber
                                                            ? "input-error"
                                                            : ""
                                                    }
                                                />
                                            </label>

                                            {checkingEndorsementNo && (
                                                <small style={{ color: "#888" }}>Checking availability...</small>
                                            )}

                                            {endorsementNoError && (
                                                <small className="error-text">{endorsementNoError}</small>
                                            )}


                                            <label className="endorsement-form-field">
                                                <span>Start Date of Endorsement <b>*</b></span>
                                                <input
                                                    type="date"
                                                    name="endorsementDate"
                                                    value={formData.endorsementDate}
                                                    onChange={handleChange}
                                                    disabled={!isPolicySelected}
                                                    className={errors.endorsementDate ? "input-error" : ""}
                                                />
                                            </label>

                                            {errors.endorsementDate && (
                                                <small className="error-text">{errors.endorsementDate}</small>
                                            )}


                                            <label className="endorsement-form-field">
                                                <span>Premium Transaction Date</span>
                                                <input
                                                    type="date"
                                                    name="premiumTransactionDate"
                                                    value={formData.premiumTransactionDate}
                                                    onChange={handleChange}
                                                    disabled={!isPolicySelected}
                                                />
                                            </label>



                                            <label className="endorsement-form-field">
                                                <span>Endorsement Type <b>*</b></span>
                                                <select
                                                    name="endorsementType"
                                                    value={formData.endorsementType}
                                                    onChange={handleChange}
                                                    disabled={!isPolicySelected}
                                                    className={errors.endorsementType ? "input-error" : ""}
                                                >

                                                    <option value="">Select Type</option>
                                                    <option value="Addition">Addition</option>
                                                    <option value="Deletion">Deletion</option>
                                                    <option value="Nil">Nil</option>
                                                </select>
                                            </label>
                                            {errors.endorsementType && (
                                                <small className="error-text">{errors.endorsementType}</small>
                                            )}



                                            {!isNil && (
                                                <label className="endorsement-form-field">
                                                    <span>{isDeletion ? "Refund Amount" : "Additional Gross Premium"} <b>*</b></span>
                                                    <input
                                                        type="number"
                                                        name="additionalGrossPremium"
                                                        value={formData.additionalGrossPremium}
                                                        onChange={handleChange}
                                                        disabled={!isPolicySelected}
                                                        className={errors.additionalGrossPremium ? "input-error" : ""}
                                                    />
                                                </label>
                                            )}


                                            {/* <label>Updated Field</label>
                                    <input
                                        type="text"
                                        name="updatedField"
                                        onChange={handleNewDataChange}
                                        disabled={!isPolicySelected}
                                        className={errors.updatedField ? "input-error" : ""}
                                    />

                                    {errors.updatedField && (
                                        <small className="error-text">{errors.updatedField}</small>
                                    )} */}

                                            <label className="endorsement-form-field full-width">
                                                <span>Remarks</span>
                                                <textarea
                                                    name="remarks"
                                                    value={formData.remarks}
                                                    onChange={handleChange}
                                                    disabled={!isPolicySelected}
                                                />
                                            </label>

                                            <label className="endorsement-form-field full-width">
                                                <span>
                                                    Upload Endorsement Document
                                                    {isApproved && <b> *</b>}
                                                </span>
                                                <input
                                                    type="file"
                                                    disabled={!isPolicySelected}
                                                    onChange={(e) => setEndorsementDoc(e.target.files[0])}
                                                    className={errors.endorsementDoc ? "input-error" : ""}
                                                />
                                            </label>

                                            {errors.endorsementDoc && (
                                                <small className="error-text">{errors.endorsementDoc}</small>
                                            )}

                                            {editData?.endorsementDocPath && canDownloadEndorsement && (
                                                <div className="endorsement-doc-view">
                                                    <button
                                                        type="button"
                                                        className="view-doc-btn"
                                                        onClick={() => handleDownloadEndorsement(editData)}
                                                    >
                                                        Download Document
                                                    </button>
                                                </div>
                                            )}

                                        </form>

                                        <div className="modal-footer-endor">
                                            <button type="submit" form="endorsement-form" className="submit-btn-endor">
                                                Submit
                                            </button>
                                            <button
                                                type="button"
                                                className="cancel-btn-endor"
                                                onClick={() => {
                                                    setShowModal(false);
                                                    resetForm();
                                                }}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>


                                </div>
                            </div>
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
};

export default Endorsement;
